import { useMutation } from "@tanstack/react-query";
import {
  ControlCode,
  pluralize,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";

export function usePartnerBlockMutation() {
  const authConfig = useAuthConfig();

  return useMutation({
    mutationFn: async ({
      controlCode,
      testId,
    }: {
      controlCode: ControlCode;
      testId: string;
    }) => {
      const response = await service(authConfig).partner.block(
        controlCode,
        testId,
      );
      return response;
    },
  });
}

export function usePartnerBlockMutationWithToast() {
  const authConfig = useAuthConfig();

  return useMutation({
    mutationFn: async ({
      controlCode,
      testId,
    }: {
      controlCode: ControlCode;
      testId: string;
    }) => {
      const response = await service(authConfig).partner.block(
        controlCode,
        testId,
      );
      return response;
    },
    onSuccess: (responses) => {
      let created = 0;
      let already_exists = 0;
      let error = 0;

      responses.forEach((response) => {
        response.rules.forEach((rule) => {
          if (rule.status === "ERROR") {
            error++;
          }
          if (rule.status === "ALREADY_EXISTS") {
            already_exists++;
          }
          if (rule.status === "CREATED") {
            created++;
          }
        });
      });

      const flatResponses = responses.flatMap((response) =>
        response.rules.map((rule) => ({
          platform: response.platform,
          ...rule,
        })),
      );

      if (error || already_exists) {
        return notifyError({
          message: (
            <Dialog>
              <div className="flex items-center justify-between gap-16">
                <span>
                  {created} {pluralize(created, "rule")} created,{" "}
                  {already_exists} {pluralize(already_exists, "rule")} rules
                  already exist, and {error} {pluralize(error, "rule")} failed
                </span>
                <DialogTrigger asChild>
                  <Button className="whitespace-nowrap border border-stroke-input bg-transparent text-white">
                    More Info
                  </Button>
                </DialogTrigger>
              </div>

              <DialogContent className="w-[500px]">
                <DialogBody className="max-h-96 overflow-y-auto">
                  <li className="flex flex-col gap-4">
                    {flatResponses.map((rule) => (
                      <ol className="flex flex-col" key={rule.name}>
                        <span>Platform: {rule.platform}</span>
                        <span>Rule: {rule.name}</span>
                        <span>Status: {rule.status}</span>
                        {rule.error && <span>Error: {rule.error}</span>}
                      </ol>
                    ))}
                  </li>
                </DialogBody>
                <DialogFooter>
                  <DialogClose>
                    <Button intent="secondary">Dismiss</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ),
          dismissible: true,
          duration: 999_999,
        });
      }

      return notifySuccess({
        message: (
          <div className="flex items-center justify-between">
            <span>
              {created} {pluralize(created, "rule")} created, {already_exists}{" "}
              {pluralize(already_exists, "rule")} rules already exist, and{" "}
              {error} {pluralize(error, "rule")} failed
            </span>
          </div>
        ),
      });
    },
    onError: (error) => {
      notifyError({
        message: "Failed to send detections",
        error,
      });
    },
  });
}
